/**
 * Every localStorage key this app owns, in one place.
 *
 * Scheme: a dotted path of camelCase segments — `<domain>.<name>[.<qualifier>]`.
 * Dynamic segments (store entity types, category API keys) go through
 * `toStorageSegment`, so `ASSET_FRAGMENT` lands as `assetFragment` and never as
 * a raw upper-snake API key.
 *
 * Not ours: `i18nextLng` is written by i18next-browser-languagedetector.
 * sessionStorage (auth/session data) is out of scope here — see
 * session-storage-helpers.
 *
 * Renaming a key? Add the old name to migrate-legacy-storage-keys so returning
 * users keep their preferences.
 */

export const THEME_KEYS = Object.freeze({
  TYPE: "theme.type",
  COLOR_PRESET: "theme.colorPreset",
  CONTRAST: "theme.contrast",
  RESPONSIVE_FONT_SIZES: "theme.responsiveFontSizes",
});

// Transient cross-tab message bus, only used when BroadcastChannel is missing.
export const AUTH_SYNC_SIGNAL_KEY = "auth.syncSignal";

// "ASSET_FRAGMENT" -> "assetFragment", "help-topics" -> "helpTopics", "APP" -> "app"
export const toStorageSegment = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/^[^a-z0-9]+/, "")
    .replace(/[^a-z0-9]+$/, "")
    .replace(/[^a-z0-9]+([a-z0-9])/g, (_, next) => next.toUpperCase());

export const sidebarWidthKey = (scope) => `sidebar.${scope}.width`;

export const SIDEBAR_KEYS = Object.freeze({
  DEFAULT: sidebarWidthKey("default"),
  ASSETS: sidebarWidthKey("assets"),
  HELP: sidebarWidthKey("help"),
  HELP_TOPICS: sidebarWidthKey("helpTopics"),
  NLV_MANAGER: sidebarWidthKey("nlvManager"),
});

/**
 * Rolodex pages persist one width per page, keyed off the rolodex store's
 * currentType (users, feedbacks, assettemplates, ...) — the same id the store
 * uses for its `<type>_rolodexState` sessionStorage entry.
 */
export const rolodexSidebarKey = (type) =>
  sidebarWidthKey(type ? `rolodex.${toStorageSegment(type)}` : "rolodex.default");

/**
 * Entity-editor sidebars persist one width per page. Entity pages key off the
 * store's entityType (zone/widget/tenant/app/tag); taxonomy pages key off the
 * category API key under a `category` namespace, so the Apps page ("app") and
 * the App categories page ("APP") never share a key.
 */
export const entityEditorSidebarKey = ({ entityType, category } = {}) => {
  if (entityType) {
    return sidebarWidthKey(`entityEditor.${toStorageSegment(entityType)}`);
  }
  if (category) {
    return sidebarWidthKey(`entityEditor.category.${toStorageSegment(category)}`);
  }
  return sidebarWidthKey("entityEditor.default");
};
